import { ActionContext, ActionTree } from "vuex";
import { Mutations } from "./mutations";
import { MutationType } from "./mutation-types";
import { State } from "./state";
import { ActionTypes } from "./action-types";
import { LoggedInPlayer } from "../protocol/dto/logged-in-player";
import appRestService from "../protocol/rest-service";
import AppConfig from "../config/app-config";
import { AbstractPlayer } from "../types/abstract-player";
import { RoomItem } from "../types/room-item";
import { store } from ".";
import gameApplication from "../game/game-app";
import { GameStage } from "../types/game-stage-enum";
import { Player } from "../game/player";
import { LoggedOutPlayer } from "../protocol/dto/logged-out-player";
import router from "../router";
import { SettingsModel } from "../types/settings-model";
import { GameConfig } from "../config/game-config";

/**
 * This is to define type for the Vuex actions augments.
 * Actions are used for asynchronous state management.
 */
type ActionAugments = Omit<ActionContext<State, State>, "commit"> & {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
};

/**
 * This is to define type for the Vuex actions.
 */
export type Actions = {
  // login and user
  [ActionTypes.USER_LOGIN](context: ActionAugments, payload: LoggedInPlayer): Promise<boolean>;
  [ActionTypes.USER_LOGOUT](context: ActionAugments, payload: LoggedOutPlayer): Promise<boolean>;
  [ActionTypes.SELECT_GAME](context: ActionAugments, payload: string): void;
  [ActionTypes.SELECT_GAME](context: ActionAugments, payload: string): void;
  [ActionTypes.SELECT_ROOM](context: ActionAugments, payload: string): void;
  [ActionTypes.ENTER_ROOM](context: ActionAugments, payload: string): void;
  [ActionTypes.USER_CONNECTED](context: ActionAugments, payload: boolean): void;

  // main game windows
  [ActionTypes.LOGIN_SHOWN](context: ActionAugments, payload: boolean): void;
  [ActionTypes.ROOMS_SHOWN](context: ActionAugments, payload: boolean): void;
};

/**
 * This class defines the Vuex actions.
 *
 * This is used for chainging the state in the Vuex store.
 * If we update any changes in the store we can fire events.
 * This class also defines the action type.
 * 
 * Action are used for asynchronous state management.
 */
export const actions: ActionTree<State, State> & Actions = {
  /**
   * This action logs the player into the game server.
   * 
   * @param param0
   * @param payload - the login details
   * @returns  either "true" for success or "false" for failure
   */
  async [ActionTypes.USER_LOGIN]({ commit }, credentials: LoggedInPlayer): Promise<boolean> {
    try {
      console.log("Vuex action USER_LOGIN");
      const res = await appRestService.post(AppConfig.LOGIN, credentials);

      if (res.data) {
        const player: AbstractPlayer = res.data.player;
        commit(MutationType.AUTH_USER, credentials);

        const gamePlayer: Player = new Player();
        gamePlayer.playerId = player.playerId;
        gamePlayer.player = player;

        gameApplication.setPlayer(gamePlayer);

        // Set the settings model from the detault configuration.
        const settings: SettingsModel = {
          enableSounds: GameConfig.settings.enableSounds,
          enableChatMessages: GameConfig.settings.enableChatMessages,
          acceptChallengesFrom: GameConfig.settings.acceptChallengesFrom, // "everyone" or "friendsOnly" 
          blockAllGameChallenges: GameConfig.settings.blockAllGameChallenges,
          blockWagerGameChallenges: GameConfig.settings.blockWagerGameChallenges,
          hitPositionSize: GameConfig.settings.hitPositionSize, // "small" or "large"
          defaultPracticeGame: GameConfig.settings.defaultPracticeGame, // game kind enumeration index
        };

        // Set the default game settings.
        commit(MutationType.SETTINGS, settings);

        // Initilise the Websocket connection.
        gameApplication.connectWebsocket(player.playerId);
      }

      // return the response
      return res;
    } catch (e) {
      console.log("Problem with Vuex action USER_LOGIN ", e);
      // return the response
      return Promise.reject(false);
    }
  },

  /**
   * This action logs the player out of the game server.
   * 
   * @param param0
   * @param payload - the logout details
   * @returns  either "true" for success or "false" for failure
   */
  async [ActionTypes.USER_LOGOUT]({ commit }, user: LoggedOutPlayer): Promise<boolean> {
    try {
      console.log("Vuex action USER_LOGOUT");
      const res = await appRestService.post(AppConfig.LOGOUT, user);

      if (res.data) {
        // Clear the user authentication.
        //@ts-ignore 
        commit(MutationType.AUTH_USER, undefined);

        // re-direct the user to the login page
        window.location.href = AppConfig.WEB_SITE_PLAY_PAGE;
      }

      // return the response
      return res;
    } catch (e) {
      console.log("Problem with Vuex action USER_LOGOUT ", e);
      // return the response
      return Promise.reject(false);
    }
  },

  /**
   * This action selects the game type e.g. POOL8 or SNOOKER etc.
   * 
   * @param param0
   * @param gameKind - the game kind
   */
  async [ActionTypes.SELECT_GAME]({ commit }, gameKind: string) {
    try {
      const player = gameApplication.getPlayer();

      const requestData = {
        username: player.playerId,
        gameKind,
      };

      const res = await appRestService.post(AppConfig.SELECT_GAME, requestData);

      if (res.data) {
        const rooms = res.data;
        
        commit(MutationType.ROOMS, rooms);
        commit(MutationType.SELECT_GAME_KIND, gameKind); // store in the Vuex mutation

        gameApplication.setGameStage(GameStage.RoomChoosing);
        gameApplication.setGameKind(gameKind);
      }
    } catch (e) {
      window.location.href = AppConfig.WEB_SITE_PLAY_PAGE;
      console.log("Problem with Vuex action SELECT_GAME ", e);
    }
  },

  /**
   * This action is executed when a user enters a game room for the game type.
   * 
   * @param param0
   * @param roomId - the game room ID
   */
  async [ActionTypes.ENTER_ROOM]({ state }, roomId: string) {
    console.log("Vuex action ENTER_ROOM " + roomId);
    try {
      const gameKind: string = state.selectedGameKind;
      const rooms: RoomItem[] = store.state.rooms;
      const room = rooms.find((r) => r.roomId === roomId);
      //@ts-ignore
      room.gameKind = gameKind;

      //@ts-ignore
      console.log("You selected rooom : ", room.roomName);

      // We have joined the room via REST. Now we can send a Websocket message.
      // From now we can mostly follow the game applet logic for the basic functionality.
      //@ts-ignore
      gameApplication.selectRoom(room);
      gameApplication.setGameStage(GameStage.InRoom);
    } catch (e) {
      window.location.href = AppConfig.WEB_SITE_PLAY_PAGE;
      console.log("Problem with Vuex action ENTER_ROOM ", e);
    }
  },

  /**
   * This action is executed when a user connets or disconnects from the game server.
   * 
   * @param param0
   * @param isConnected - "true" if the user is connected and "false" if the user is disconnected
   */
  async [ActionTypes.USER_CONNECTED]({ commit }, isConnected: boolean) {
    commit(MutationType.USER_CONNECTED, isConnected);

    if (!isConnected) {
      // if the user is disconnected
      const player = gameApplication.getPlayer();
      const requestData = {
        username: player.playerId,
      };

      // If the user is disconnected log the user out via REST.
      const res = await appRestService.post(AppConfig.LOGOUT, requestData);

      if (res.data && res.data) {
        if (res.data === "true") {
          // Clear the user authentication.
          //@ts-ignore 
          commit(MutationType.AUTH_USER, undefined);

          console.log("Vuex action USER_DISCONNECTED ");
        } else if (res.data === "false") {
          console.log("Problem with Vuex action USER_DISCONNECT");
        }
      }
    }
  },

  /**
   * This is used to send the "select room" event to the Vue.js front end.
   * 
   * @param param0
   * @param roomId - the game room ID
   */
  async [ActionTypes.SELECT_ROOM]({ commit }, roomId: string) {
    commit(MutationType.SELECT_ROOM, roomId);
  },

  // main game windows
  async [ActionTypes.LOGIN_SHOWN]({ commit }, payload: boolean) {
    commit(MutationType.LOGIN_SHOWN, payload);
  },
  async [ActionTypes.ROOMS_SHOWN]({ commit }, payload: boolean) {
    commit(MutationType.ROOMS_SHOWN, payload);
  },

  // settings modal
  async [ActionTypes.SETTINGS]({ commit }, payload: SettingsModel) {
    commit(MutationType.SETTINGS, payload);
  },
};

